/**
 * Quality Gate 测试用例管理 Tab
 * 管理和执行 Facet 的测试用例
 * 
 * 参考：API 文档 第 9 节 Quality Gate 接口
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Progress } from '@/shared/components/ui/progress'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { cn } from '@/shared/lib/utils'
import {
  Shield,
  Plus,
  Play,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  AlertCircle,
  Rocket,
  Pencil,
  MessageSquare,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { FacetApiService } from '@/modules/identity/services'
import type {
  TestCase,
  TestCaseType,
  ExpectedBehavior,
  QualityGateSummary,
  TestResultResponse,
  CreateTestCaseRequest,
  SceneTag,
} from '@/modules/identity/types/facet'
import {
  TEST_CASE_TYPE_CONFIG,
  TEST_CASE_TYPES,
  EXPECTED_BEHAVIOR_CONFIG,
} from '@/modules/identity/types/facet'

interface QualityGateTabProps {
  identityId: string
  sceneId: SceneTag
  canPublish?: boolean
  onPublish?: () => void
}

/**
 * Quality Gate Tab
 */
export function QualityGateTab({
  identityId,
  sceneId,
  canPublish,
  onPublish,
}: QualityGateTabProps) {
  const { showSuccess, showError } = useToast()

  // 数据状态
  const [testCases, setTestCases] = useState<TestCase[]>([])
  const [summary, setSummary] = useState<QualityGateSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 测试运行状态
  const [testResult, setTestResult] = useState<TestResultResponse | null>(null)
  const [running, setRunning] = useState(false)

  // 弹窗状态
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [caseToDelete, setCaseToDelete] = useState<TestCase | null>(null)
  const [caseToEdit, setCaseToEdit] = useState<TestCase | null>(null)
  
  // 手动测试弹窗
  const [manualTestOpen, setManualTestOpen] = useState(false)
  const [manualTestInput, setManualTestInput] = useState('')
  const [manualTestResult, setManualTestResult] = useState<string | null>(null)
  const [manualTesting, setManualTesting] = useState(false)

  // 创建/编辑表单
  const [formData, setFormData] = useState<{
    type: TestCaseType
    category: string
    input: string
    expected_behavior: ExpectedBehavior
  }>({
    type: 'boundary',
    category: '',
    input: '',
    expected_behavior: 'refuse',
  })
  const [saving, setSaving] = useState(false)
  
  // 兼容旧代码
  const createForm = formData
  const setCreateForm = setFormData
  const creating = saving

  // ==================== 数据加载 ====================
  const loadTestCases = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await FacetApiService.listTestCases(identityId, sceneId)
      // 防御性编程
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any
      const items = responseData?.items || responseData?.data || []
      setTestCases(items)
      setSummary(responseData?.summary || null)
    } catch (err) {
      console.error('Failed to load test cases:', err)
      setError('无法加载测试用例')
    } finally {
      setLoading(false)
    }
  }, [identityId, sceneId])

  useEffect(() => {
    loadTestCases()
  }, [loadTestCases])

  // ==================== 操作处理 ====================

  /**
   * 运行测试
   */
  const handleRunTests = useCallback(async () => {
    setRunning(true)
    setTestResult(null)
    try {
      const response = await FacetApiService.runTests(identityId, sceneId)
      showSuccess('测试已启动', '开始测试')
      
      // 轮询结果
      const jobId = response.data.job_id
      const pollResult = async () => {
        try {
          const resultResponse = await FacetApiService.getTestResults(identityId, sceneId, jobId)
          setTestResult(resultResponse.data)
          
          if (resultResponse.data.status === 'running') {
            setTimeout(pollResult, 2000)
          } else {
            setRunning(false)
            loadTestCases() // 刷新列表更新 last_result
          }
        } catch {
          setRunning(false)
        }
      }
      pollResult()
    } catch (err) {
      console.error('Failed to run tests:', err)
      showError('启动测试失败', '操作失败')
      setRunning(false)
    }
  }, [identityId, sceneId, showSuccess, showError, loadTestCases])

  /**
   * 创建测试用例
   */
  const handleCreate = useCallback(async () => {
    if (!formData.input.trim() || !formData.category.trim()) {
      showError('请填写完整信息', '验证失败')
      return
    }

    setSaving(true)
    try {
      const request = {
        type: formData.type,
        category: formData.category.trim(),
        input: formData.input.trim(),
        expected_behavior: formData.expected_behavior,
      } as CreateTestCaseRequest
      await FacetApiService.createTestCase(identityId, sceneId, request)
      showSuccess('测试用例已创建', '创建成功')
      setCreateDialogOpen(false)
      setFormData({
        type: 'boundary',
        category: '',
        input: '',
        expected_behavior: 'refuse',
      })
      loadTestCases()
    } catch (err) {
      console.error('Failed to create test case:', err)
      showError('创建失败', '操作失败')
    } finally {
      setSaving(false)
    }
  }, [identityId, sceneId, formData, showSuccess, showError, loadTestCases])

  /**
   * 打开编辑弹窗
   */
  const handleEditClick = useCallback((testCase: TestCase) => {
    setCaseToEdit(testCase)
    setFormData({
      type: testCase.type,
      category: testCase.category,
      input: testCase.input || '',
      expected_behavior: testCase.expected_behavior || 'answer',
    })
    setEditDialogOpen(true)
  }, [])

  /**
   * 保存编辑
   */
  const handleEditSave = useCallback(async () => {
    if (!caseToEdit) return
    if (!formData.input.trim() || !formData.category.trim()) {
      showError('请填写完整信息', '验证失败')
      return
    }

    setSaving(true)
    try {
      await FacetApiService.updateTestCase(identityId, sceneId, caseToEdit.id, {
        input: formData.input.trim(),
        expected_behavior: formData.expected_behavior,
      })
      showSuccess('测试用例已更新', '保存成功')
      setEditDialogOpen(false)
      setCaseToEdit(null)
      setFormData({
        type: 'boundary',
        category: '',
        input: '',
        expected_behavior: 'refuse',
      })
      loadTestCases()
    } catch (err) {
      console.error('Failed to update test case:', err)
      showError('更新失败', '操作失败')
    } finally {
      setSaving(false)
    }
  }, [identityId, sceneId, caseToEdit, formData, showSuccess, showError, loadTestCases])

  /**
   * 手动测试
   */
  const handleManualTest = useCallback(async () => {
    if (!manualTestInput.trim()) {
      showError('请输入测试内容', '验证失败')
      return
    }

    setManualTesting(true)
    setManualTestResult(null)
    try {
      // 调用手动测试接口
      const response = await FacetApiService.manualTest(identityId, sceneId, {
        input: manualTestInput.trim(),
      })
      setManualTestResult(response.data?.output || response.data?.response || '无响应')
    } catch (err) {
      console.error('Failed to run manual test:', err)
      showError('测试失败', '操作失败')
      setManualTestResult('测试失败，请重试')
    } finally {
      setManualTesting(false)
    }
  }, [identityId, sceneId, manualTestInput, showError])

  /**
   * 删除测试用例
   */
  const handleDeleteClick = useCallback((testCase: TestCase) => {
    setCaseToDelete(testCase)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!caseToDelete) return

    try {
      await FacetApiService.deleteTestCase(identityId, sceneId, caseToDelete.id)
      showSuccess('测试用例已删除', '删除成功')
      setDeleteDialogOpen(false)
      setCaseToDelete(null)
      loadTestCases()
    } catch (err) {
      console.error('Failed to delete test case:', err)
      showError('删除失败', '操作失败')
    }
  }, [identityId, sceneId, caseToDelete, showSuccess, showError, loadTestCases])

  // ==================== 渲染 ====================
  return (
    <div className="space-y-4">
      {/* 测试概览 */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-violet-500" />
                Quality Gate
              </CardTitle>
              <CardDescription>测试用例管理和执行</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadTestCases()}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                添加用例
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setManualTestInput('')
                  setManualTestResult(null)
                  setManualTestOpen(true)
                }}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                手动测试
              </Button>
              <Button
                size="sm"
                onClick={handleRunTests}
                disabled={running || testCases.length === 0}
              >
                {running ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                运行测试
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* 统计信息 */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-800">{summary.total}</p>
                <p className="text-xs text-slate-500">总用例</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-600">{summary.passed}</p>
                <p className="text-xs text-emerald-600">通过</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{summary.failed}</p>
                <p className="text-xs text-red-600">失败</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-600">{summary.pending || 0}</p>
                <p className="text-xs text-slate-500">待测试</p>
              </div>
              <div className="p-3 bg-violet-50 rounded-lg text-center">
                <p className={cn(
                  'text-2xl font-bold',
                  summary.pass_rate >= 0.9 ? 'text-emerald-600' :
                  summary.pass_rate >= 0.7 ? 'text-amber-600' : 'text-red-600'
                )}>
                  {Math.round(summary.pass_rate * 100)}%
                </p>
                <p className="text-xs text-violet-600">通过率</p>
              </div>
            </div>
          )}

          {/* 测试运行进度 */}
          {running && testResult && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-blue-800">测试运行中...</span>
              </div>
              <Progress
                value={testResult.summary.total > 0 
                  ? ((testResult.summary.passed + testResult.summary.failed) / testResult.summary.total) * 100 
                  : 0}
                className="h-2"
              />
            </div>
          )}

          {/* 测试结果：可发布检查 */}
          {testResult && testResult.status === 'completed' && (
            <div className={cn(
              'mb-6 p-4 rounded-lg',
              testResult.can_publish ? 'bg-emerald-50' : 'bg-amber-50'
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {testResult.can_publish ? (
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  )}
                  <span className={cn(
                    'font-medium',
                    testResult.can_publish ? 'text-emerald-800' : 'text-amber-800'
                  )}>
                    {testResult.can_publish ? '✅ 所有测试通过，可以发布' : '⚠️ 测试未通过，无法发布'}
                  </span>
                </div>
                {testResult.can_publish && canPublish && onPublish && (
                  <Button size="sm" onClick={onPublish}>
                    <Rocket className="h-4 w-4 mr-2" />
                    发布 Facet
                  </Button>
                )}
              </div>
              {testResult.blocking_issues.length > 0 && (
                <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                  {testResult.blocking_issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* 用例列表 */}
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-violet-600" />
              <p className="text-slate-500">加载中...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
              <p className="text-slate-600 mb-4">{error}</p>
              <Button variant="outline" onClick={() => loadTestCases()}>
                重试
              </Button>
            </div>
          ) : testCases.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>暂无测试用例</p>
              <p className="text-sm mt-2">添加测试用例以进行 Quality Gate 测试</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">类型</TableHead>
                    <TableHead className="w-32">分类</TableHead>
                    <TableHead>输入</TableHead>
                    <TableHead className="w-28">期望行为</TableHead>
                    <TableHead className="w-24">结果</TableHead>
                    <TableHead className="w-20">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testCases.map((testCase) => (
                    <TableRow key={testCase.id}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {TEST_CASE_TYPE_CONFIG[testCase.type]?.icon} {TEST_CASE_TYPE_CONFIG[testCase.type]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{testCase.category}</TableCell>
                      <TableCell>
                        <p className="text-sm line-clamp-2">{testCase.input || (testCase.turns && `多轮对话 (${testCase.turns.length} 轮)`)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {EXPECTED_BEHAVIOR_CONFIG[testCase.expected_behavior || 'answer']?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {testCase.last_result ? (
                          testCase.last_result.passed ? (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="h-4 w-4" /> 通过
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600">
                              <XCircle className="h-4 w-4" /> 失败
                            </span>
                          )
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400">
                            <Clock className="h-4 w-4" /> 待测试
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-slate-600 hover:bg-slate-50"
                            onClick={() => handleEditClick(testCase)}
                            title="编辑"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteClick(testCase)}
                            title="删除"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 创建测试用例弹窗 */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>添加测试用例</DialogTitle>
            <DialogDescription>
              创建一个新的 Quality Gate 测试用例
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>类型</Label>
                <Select
                  value={createForm.type}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, type: v as TestCaseType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEST_CASE_TYPES.filter(t => t !== 'multi_turn').map(type => (
                      <SelectItem key={type} value={type}>
                        {TEST_CASE_TYPE_CONFIG[type].icon} {TEST_CASE_TYPE_CONFIG[type].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>期望行为</Label>
                <Select
                  value={createForm.expected_behavior}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, expected_behavior: v as ExpectedBehavior }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['answer', 'refuse', 'deflect'] as ExpectedBehavior[]).map(behavior => (
                      <SelectItem key={behavior} value={behavior}>
                        {EXPECTED_BEHAVIOR_CONFIG[behavior].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>分类 *</Label>
              <Input
                value={createForm.category}
                onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                placeholder="如：隐私问题、边界测试等"
              />
            </div>
            <div className="space-y-2">
              <Label>测试输入 *</Label>
              <Textarea
                value={createForm.input}
                onChange={(e) => setCreateForm(prev => ({ ...prev, input: e.target.value }))}
                placeholder="输入测试问题或对话..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 编辑测试用例弹窗 */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open)
        if (!open) {
          setCaseToEdit(null)
          setFormData({
            type: 'boundary',
            category: '',
            input: '',
            expected_behavior: 'refuse',
          })
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>编辑测试用例</DialogTitle>
            <DialogDescription>
              修改 Quality Gate 测试用例
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>类型</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, type: v as TestCaseType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEST_CASE_TYPES.filter(t => t !== 'multi_turn').map(type => (
                      <SelectItem key={type} value={type}>
                        {TEST_CASE_TYPE_CONFIG[type].icon} {TEST_CASE_TYPE_CONFIG[type].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>期望行为</Label>
                <Select
                  value={formData.expected_behavior}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, expected_behavior: v as ExpectedBehavior }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['answer', 'refuse', 'deflect'] as ExpectedBehavior[]).map(behavior => (
                      <SelectItem key={behavior} value={behavior}>
                        {EXPECTED_BEHAVIOR_CONFIG[behavior].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>分类 *</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="如：隐私问题、边界测试等"
              />
            </div>
            <div className="space-y-2">
              <Label>测试输入 *</Label>
              <Textarea
                value={formData.input}
                onChange={(e) => setFormData(prev => ({ ...prev, input: e.target.value }))}
                placeholder="输入测试问题或对话..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleEditSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 手动测试弹窗 */}
      <Dialog open={manualTestOpen} onOpenChange={setManualTestOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-violet-500" />
              手动测试
            </DialogTitle>
            <DialogDescription>
              输入任意问题，测试当前 Facet 的响应效果
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>测试输入</Label>
              <Textarea
                value={manualTestInput}
                onChange={(e) => setManualTestInput(e.target.value)}
                placeholder="输入你想测试的问题..."
                rows={3}
              />
            </div>
            {manualTestResult && (
              <div className="space-y-2">
                <Label>AI 响应</Label>
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{manualTestResult}</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualTestOpen(false)}>
              关闭
            </Button>
            <Button onClick={handleManualTest} disabled={manualTesting}>
              {manualTesting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              发送测试
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除这个测试用例吗？此操作无法撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
